import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { GetTranslateUiUrl } from './utils/URLUtils';



// Initialize i18n with default configuration
i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        fallbackLng: 'en',
        debug: false,// process.env.NODE_ENV === 'development',
        interpolation: {
            escapeValue: false,
        },
        react: {
            useSuspense: false,
            transEmptyNodeValue: '',
            transSupportBasicHtmlNodes: true,
            transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
        },
        backend: {
            loadPath: (lng: any) => {
                if (lng[0] === 'en') return;
                const language = String(lng || '');
                if (!language) {
                    return '';
                }
                return GetTranslateUiUrl(language);
            },
            parse: (data: string) => {
                if (!data) return;
                const lines = data.split('\n');
                const translations: { [key: string]: string } = {};

                lines.forEach(line => {
                    const [key, value] = line.split('\t');
                    if (key && value) {
                        const trimmedKey = key.trim();
                        const trimmedValue = value.trim();
                        translations[trimmedKey] = trimmedValue;
                    }
                });

                return translations;
            },
            requestOptions: {
                cache: 'no-store'
            }
        },
        // Enable missing key handling
        saveMissing: true,
        missingKeyHandler: (lngs: readonly string[], ns: string, key: string, fallbackValue: string) => {
            // If translations are disabled or it's English, return the key itself
            if (i18n.language === 'en') {
                return key;
            }

            // Store original key for comma restoration
            const originalKey = key;
            // Remove commas from numbers for lookup
            const lookupKey = key.replace(/(\d+),(\d+)/g, '$1$2');

            // If the key contains numbers or quoted strings
            if (/\d/.test(lookupKey) || /"[^"]*"/.test(lookupKey)) {
                // Convert version numbers and quoted strings
                const convertedKey = lookupKey
                    .replace(/\d+(\.\d+)*/g, '8')
                    .replace(/"([^"]*)"/g, '"On The Road"');
                const resources = i18n.getResourceBundle(i18n.language, ns);
                const translation = resources?.[convertedKey];

                if (translation) {
                    let result = translation;

                    // Replace '8' with original version numbers
                    const versionMatch = lookupKey.match(/\d+(\.\d+)*/);
                    if (versionMatch) {
                        result = result.replace('8', versionMatch[0]);
                    }

                    // Replace any quoted strings in the result with original quoted strings
                    const originalQuotedStrings = lookupKey.match(/"([^"]*)"/g) || [];
                    const resultQuotedStrings = result.match(/"([^"]*)"/g) || [];

                    if (originalQuotedStrings.length > 0 && resultQuotedStrings.length > 0) {
                        originalQuotedStrings.forEach((original, index) => {
                            if (resultQuotedStrings[index]) {
                                result = result.replace(resultQuotedStrings[index], original);
                            }
                        });
                    }

                    // Restore commas in the result if they existed in the original key
                    const commaMatches = originalKey.match(/(\d+),(\d+)/g);
                    if (commaMatches) {
                        commaMatches.forEach(match => {
                            const numberWithoutComma = match.replace(/,/g, '');
                            result = result.replace(numberWithoutComma, match);
                        });
                    }

                    i18n.addResource(i18n.language, ns, key, result);
                    return result;
                }
            }

            // Try to find the exact key
            const resources = i18n.getResourceBundle(i18n.language, ns);
            const exactTranslation = resources?.[key];
            if (exactTranslation) {
                i18n.addResource(i18n.language, ns, key, exactTranslation);
                return exactTranslation;
            }

            return fallbackValue;
        }
    });

// Function to initialize translations for a specific language
export const initializeTranslations = (languageCode: string | undefined, shouldTranslate: boolean) => {
    try {
        if (!languageCode || languageCode.toLowerCase() === 'en' || !shouldTranslate) {
            i18n.changeLanguage('en');
            return;
        }

        i18n.changeLanguage(languageCode);
    } catch (error) {
        console.error('Error initializing translations:', error);
        i18n.changeLanguage('en');
    }
};

export default i18n;